import React, { useState, useEffect, useMemo, useRef, memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MemoizedDealCard } from "./DealCard";
import AdShortcode from "./AdShortcode";
// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────

const MONTHS = [
  "01 January","02 February","03 March","04 April","05 May","06 June",
  "07 July","08 August","09 September","10 October","11 November","12 December",
];

const SELECT_CHEVRON_STYLE = {
  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3Csvg width='12' height='8' viewBox='0 0 12 8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23333' stroke-width='2' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "12px",
  backgroundPosition: "right 14px center",
};

const WHATSAPP_NUMBER = "918438506813";
const ITEMS_PER_PAGE = 12;
const AD_INTERVAL = 6;

// ─────────────────────────────────────────────────────────────
// TRAVEL LOGO (WhatsApp icon in coupon card)
// ─────────────────────────────────────────────────────────────

function WhatsAppLogoIcon({ className }) {
  return (
    <svg viewBox="0 0 192 192" className={className} fill="currentColor" aria-hidden="true">
      <path d="m60.359 160.867 2.894-5.256a6.003 6.003 0 0 0-4.284-.581l1.39 5.837ZM22 170l-5.837-1.39a6.002 6.002 0 0 0 7.227 7.227L22 170Zm9.133-38.359 5.837 1.39a6.001 6.001 0 0 0-.581-4.284l-5.256 2.894ZM96 176c44.183 0 80-35.817 80-80h-12c0 37.555-30.445 68-68 68v12Zm-38.535-9.877C68.9 172.42 82.04 176 96 176v-12c-11.884 0-23.04-3.043-32.747-8.389l-5.788 10.512Zm-34.075 9.714 38.358-9.133-2.78-11.674-38.358 9.133 2.78 11.674Zm1.906-45.585-9.133 38.358 11.674 2.78 9.133-38.359-11.674-2.779ZM16 96c0 13.959 3.58 27.1 9.877 38.535l10.512-5.788C31.043 119.039 28 107.884 28 96H16Zm80-80c-44.183 0-80 35.817-80 80h12c0-37.555 30.445-68 68-68V16Zm80 80c0-44.183-35.817-80-80-80v12c37.555 0 68 30.445 68 68h12Z" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// FILTER BAR
// ─────────────────────────────────────────────────────────────

function FilterBar({ activeCategory, setActiveCategory, categories, darkMode = false }) {
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== "undefined" ? window.matchMedia("(max-width: 640px)").matches : false
  ));

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const media = window.matchMedia("(max-width: 640px)");
    const onChange = () => setIsMobile(media.matches);
    onChange();
    if (media.addEventListener) {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  const orderedCategories = useMemo(() => {
    if (!isMobile) return categories;
    const list = Array.isArray(categories) ? [...categories] : [];
    const idx = list.indexOf("Indian Escapes Deals");
    if (idx <= 0) return list;
    const [item] = list.splice(idx, 1);
    list.splice(1, 0, item); // right after "All"
    return list;
  }, [categories, isMobile]);

  return (
    <div className="trw-filter-bar mb-[30px]">
      <div
        className="flex flex-wrap items-center justify-center gap-3 text-center"
        style={{ fontFamily: "Poppins, sans-serif" }}
        aria-label="Super deals filters"
      >
        {orderedCategories.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setActiveCategory(cat)}
            aria-pressed={activeCategory === cat}
            className={`trw-filter-btn flex-shrink-0 cursor-pointer rounded-[12px] border px-[10px] py-[8px] text-[14px] font-[400] leading-[20px] transition-colors sm:px-4 sm:py-[11px] ${
              activeCategory === cat
                ? "border-[#d02525] text-[#d02525]"
                : darkMode 
                  ? "border-white text-white hover:border-[#d02525] hover:text-[#d02525]"
                  : "border-black text-black hover:border-[#d02525] hover:text-[#d02525]"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DEAL POPUP FORM
// ─────────────────────────────────────────────────────────────

function DealPopupForm({
  deal,
  close,
  onSuccess,
  tagText = "Super Deals",
  submitButtonText = "Unlock Deal",
  headingText = "Your Deal Awaits!",
  subtitleText = "Unlock a special discount on your travel.",
}) {
  const [formData, setFormData] = useState({
    fullname: "",
    phone: "",
    travel_month: "",
    location: "",
    people_count: "",
    budget: "",
    planning_stage: "",
    message: "",
  });

  const requiredFields = ["fullname", "phone", "travel_month", "location", "people_count", "budget", "planning_stage"];

  useEffect(() => {
    const monthIdx = new Date().getMonth();
    setFormData((prev) => ({
      ...prev,
      location: deal?.displayTitle || deal?.title || "",
      travel_month: MONTHS[monthIdx],
    }));
  }, [deal]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const filledCount = requiredFields.filter((f) => String(formData[f] || "").trim()).length;
  const progressPct = Math.max(15, Math.round((filledCount / requiredFields.length) * 100));

  const getValidity = (desc) => {
    const m = desc?.match(/Valid till:\s*([^.|]+)/i);
    return m ? m[1].trim() : "Limited Period";
  };

  const getDiscount = (d) => {
    if (d?.discount) return d.discount;
    const couponMatch = (d?.coupon || "").match(/(\d{2})$/);
    if (couponMatch) return `${couponMatch[1]}%`;
    const desc = d?.description || "";
    if (desc.includes("₹30,000") || desc.includes("30,000")) return "50%";
    if (desc.includes("₹20,000") || desc.includes("20,000")) return "40%";
    if (desc.includes("₹15,000") || desc.includes("15,000")) return "30%";
    if (desc.includes("₹7,500") || desc.includes("7,500")) return "25%";
    if (desc.includes("₹5,000") || desc.includes("5,000")) return "20%";
    return "25%";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const monthNum = formData.travel_month.split(" ")[0] || "01";
    const travelDate = `${new Date().getFullYear()}-${monthNum}-01`;
    const couponCode = deal?.coupon || `TravelRethinkWays${String(deal?.id || 1).padStart(2, "0")}`;

    const body = new FormData();
    body.append("fullname", formData.fullname);
    body.append("phone", formData.phone);
    body.append("location", formData.location);
    body.append("people_count", formData.people_count);
    body.append("budget", formData.budget);
    body.append("planning_stage", formData.planning_stage);
    body.append("message", formData.message);
    body.append("travel_date", travelDate);
    body.append("coupon_code", couponCode);

    try {
      const res = await fetch("https://gdbrandcreative.com/Travel/scripts/deal.php", {
        method: "POST",
        body,
      });
      const json = await res.json();
      if (json.status === "success") {
        onSuccess({
          coupon: couponCode,
          discountPercent: getDiscount(deal),
          validity: deal?.validUntil || getValidity(deal?.displayDescription || deal?.description),
        });
        return;
      }
      alert(`Error: ${json.message || "Unable to submit form."}`);
    } catch {
      alert("Network error. Please check your connection and try again.");
    }
  };

  const inputClass =
    "h-[46px] w-full rounded-[12px] border border-[#ececec] bg-[#fafafa] px-[14px] py-3 text-sm text-[#151515] outline-none transition-all focus:border-[#D02525] focus:bg-white max-[640px]:h-11";
  const selectClass =
    "h-[46px] w-full appearance-none rounded-[12px] border border-[#ececec] bg-[#fafafa] px-[14px] py-3 pr-10 text-sm leading-[1.4] text-[#333] outline-none transition-all focus:border-[#D02525] focus:bg-white max-[640px]:h-11";

  return (
    <div className="trw-deal-popup fixed inset-0 z-[9998] flex items-center justify-center bg-[rgba(10,10,10,0.78)] p-4 max-[640px]:py-2">
      <div className="trw-deal-popup-card relative w-[90%] max-w-[500px] rounded-[22px] border border-[#eadede] bg-white p-0 shadow-[0_30px_80px_rgba(0,0,0,0.32)]">
        {/* Close button */}
        <button
          type="button"
          onClick={close}
          className="trw-deal-popup-close absolute top-[8px] right-[14px] z-[99999] inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-[#f0d7d7] bg-white text-[20px] leading-none font-normal text-[#D02525] shadow-[0_8px_20px_rgba(208,37,37,0.12)]"
          aria-label="Close form popup"
        >
          ✕
        </button>

        {/* Header */}
        <div className="px-[28px] pt-[22px] max-[640px]:px-4 max-[640px]:pt-4">
          <div className="mb-5 h-[4px] w-full overflow-hidden rounded-full bg-[#ececec] max-[640px]:mb-4">
            <div
              className="h-full rounded-full bg-[#D02525] transition-[width] duration-300 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className="mb-3 inline-flex rounded-full border border-[#f1caca] bg-[#fff5f5] px-3 py-1.5 text-[11px] font-[700] tracking-[0.08em] uppercase text-[#D02525]">
            {tagText}
          </span>
        </div>

        {/* Body */}
        <div className="px-[28px] pb-[28px] max-[640px]:px-4 max-[640px]:pb-4">
          <h2 className="mb-2 pr-10 text-left text-[30px] leading-[1.02] font-[900] tracking-[0.02em] text-[#151515] uppercase max-[640px]:mt-[10px] max-[640px]:text-[24px] max-[640px]:leading-tight">
            {headingText}
          </h2>
          <p className="mb-5 max-w-[380px] text-left text-[14px] font-[400] tracking-[0.1px] text-[#666] max-[640px]:mb-4 max-[640px]:text-[13px]">
            {subtitleText}
          </p>
          <div className="mb-5 flex min-h-px w-full items-center bg-[#ececec] max-[640px]:mb-4" />

          <form onSubmit={handleSubmit} className="flex flex-col gap-5 max-[640px]:gap-3">
            <div className="grid grid-cols-2 gap-x-5 gap-y-[18px] max-[640px]:grid-cols-1 max-[640px]:gap-y-2">
              <input
                type="text"
                name="fullname"
                placeholder="Name"
                value={formData.fullname}
                onChange={handleChange}
                required
                className={inputClass}
              />
              <input
                type="tel"
                name="phone"
                placeholder="Mobile"
                value={formData.phone}
                onChange={handleChange}
                required
                className={inputClass}
              />
              <select
                name="travel_month"
                value={formData.travel_month}
                onChange={handleChange}
                required
                className={selectClass}
                style={SELECT_CHEVRON_STYLE}
              >
                <option value="">Travel Month</option>
                {MONTHS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <input
                type="text"
                name="location"
                placeholder="Location"
                value={formData.location}
                onChange={handleChange}
                required
                className={inputClass}
              />
              <select
                name="people_count"
                value={formData.people_count}
                onChange={handleChange}
                required
                className={selectClass}
                style={SELECT_CHEVRON_STYLE}
              >
                <option value="">How many people?</option>
                {["1 Person","2 People","3 People","4 People","5 People","6 People","7 People","8 People","9 People","10 People","More than 10 People"].map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
              <select
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                required
                className={selectClass}
                style={SELECT_CHEVRON_STYLE}
              >
                <option value="">Budget per person (INR)</option>
                {["Less than ₹25,000","₹25,000 – ₹50,000","₹50,000 – ₹1 Lakh","₹1 Lakh – ₹2 Lakh","₹2 Lakh – ₹3 Lakh","₹3 Lakh – ₹5 Lakh","More than ₹5 Lakh"].map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </div>

            <select
              name="planning_stage"
              value={formData.planning_stage}
              onChange={handleChange}
              required
              className={selectClass}
              style={SELECT_CHEVRON_STYLE}
            >
              <option value="">What stage of planning?</option>
              {["Still dreaming / Researching","Definitely traveling","Not sure which country yet","I want to book a trip","Looking for quotes","Ready to book"].map((o) => (
                <option key={o}>{o}</option>
              ))}
            </select>

            <textarea
              name="message"
              placeholder="Message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="h-[92px] w-full resize-none rounded-[12px] border border-[#ececec] bg-[#fafafa] px-[14px] py-3 text-sm leading-[1.5] text-[#151515] outline-none transition-all focus:border-[#D02525] focus:bg-white max-[640px]:h-[78px]"
            />

            <div className="mt-1 grid grid-cols-2 gap-3 max-[640px]:grid-cols-1">
              <button
                type="button"
                onClick={close}
                className="inline-flex min-h-[50px] w-full items-center justify-center rounded-[12px] border border-[#eadede] bg-white px-4 text-center text-[14px] font-[700] text-[#4b5563] transition-colors hover:border-[#D02525] hover:text-[#D02525]"
              >
                Go Back
              </button>
              <button
                type="submit"
                className="deals seemorelink inline-flex min-h-[50px] w-full cursor-pointer items-center justify-center gap-2 rounded-[12px] border border-[#D02525] bg-[#D02525] px-4 text-center text-[16px] leading-none font-[700] text-white transition-colors hover:bg-[#b71f1f]"
              >
                {submitButtonText}
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 17 17 7" />
                  <path d="M9 7h8v8" />
                </svg>
              </button>
            </div>
          </form>

          <p className="mt-3 text-center text-[10px] text-[#777]">
            By continuing, you accept Travel Rethink Ways' Terms &amp; Conditions and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// COUPON SUCCESS POPUP
// ─────────────────────────────────────────────────────────────

function CouponPopup({ coupon, discountPercent, validity, close }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(coupon);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const waUrl = `https://wa.me/?text=${encodeURIComponent(`Use this coupon code ${coupon}`)}`;

  return (
    <div className="fixed inset-0 z-[99998] flex items-center justify-center bg-black/60 p-4">
      <div className="travelrethinkways-popup-box relative rounded-[14px] bg-transparent">
        <button
          type="button"
          onClick={close}
          className="absolute top-[10px] right-[10px] z-[99999] cursor-pointer rounded-full border border-white px-[8px] py-[4px] text-[25px] leading-none font-[100] text-black"
          aria-label="Close coupon popup"
        >
          x
        </button>
        <div className="coupon trw-coupon-card flex h-[215px] w-[375px] flex-col items-center justify-center rounded-[10px] bg-white p-[25px] text-center shadow-[0_3px_5px_rgba(0,0,0,0.5)]">
          <p className="m-0 text-[11px] font-[900] tracking-[1px] text-[darkslategrey] capitalize">
            Enquiry Submitted Successfully!
          </p>
          <p className="m-0 text-[22px] leading-[1.5] font-[700] text-red-600 [font-family:Fraunces,serif]">
            <span className="font-[900] text-red-600">{discountPercent}</span> OFFER VALIDITY Till{" "}
            <span>{validity}</span>
          </p>
          <div className="mt-[10px] mb-5 flex min-h-px w-full items-center bg-[linear-gradient(270deg,hsl(0_0%_100%_/_0.05)_-0.13%,hsl(0_0%_88%_/_0.05)_0%,#e0e0e0_49.17%,hsl(0_0%_88%_/_0.05)_100%)]" />
          <div className="flex items-center gap-2">
            <a
              href={waUrl}
              target="_self"
              rel="noopener noreferrer"
              className="inline-flex h-[34px] w-1/2 items-center justify-center gap-[15px] rounded-[6px] bg-[#25D366] text-base font-[900] text-white no-underline hover:bg-[#1ebe5d]"
              title="Share on WhatsApp"
            >
              <span>Book Now</span>
              <WhatsAppLogoIcon className="h-[21px] w-[21px]" />
            </a>
            <div className="flex h-[34px] w-1/2 items-center justify-between gap-[10px] rounded-[8px] bg-[#efefef] px-3">
              <span className="text-[16px] font-[700] uppercase tracking-[0.5px] text-black">{coupon}</span>
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-[6px]"
                aria-label="Copy coupon code"
              >
                <i className={copied ? "fa fa-check text-[20px] text-[#16c60c]" : "fa fa-copy text-[20px] text-[#555]"} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// AD PLACEHOLDER (replaces AdShortcode)
// ─────────────────────────────────────────────────────────────

function DefaultAdComponent({ shortcode }) {
  return (
    <div className="col-span-1 md:col-span-2 lg:col-span-3">
      <AdShortcode shortcode={shortcode} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SUPER DEALS PAGE
// ─────────────────────────────────────────────────────────────

/**
 * SuperDealsPage
 *
 * Props:
 *   deals        {Array}    – full deal list (from getDeals())
 *   DealCardComponent {React.ComponentType} – your MemoizedDealCard
 *   AdComponent  {React.ComponentType}      – optional ad component; defaults to DefaultAdComponent
 */
export default function SuperDealsPage({
  deals: allDeals = [],
  darkMode = false,
  DealCardComponent,
  AdComponent = DefaultAdComponent,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialCategory = searchParams.get("cat") || "All";

  const [deals, setDeals] = useState(allDeals);
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [activeDeal, setActiveDeal] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showCoupon, setShowCoupon] = useState(false);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [couponData, setCouponData] = useState({
    coupon: "",
    discountPercent: "25%",
    validity: "Limited Period",
  });

  const sentinelRef = useRef(null);

  // Re-sync if allDeals prop changes (e.g. after remote hydration)
  useEffect(() => {
    setDeals(allDeals);
  }, [allDeals]);

  useEffect(() => {
    if (initialCategory && initialCategory !== activeCategory) {
      setActiveCategory(initialCategory);
      setVisibleCount(ITEMS_PER_PAGE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCategory]);

  // Listen for trw-super-deals-updated custom event
  useEffect(() => {
    const handler = () => {
      // If a global getDeals fn is available, refresh
      if (typeof window !== "undefined" && typeof window.__trwGetDeals === "function") {
        setDeals(window.__trwGetDeals());
      }
    };
    window.addEventListener("trw-super-deals-updated", handler);
    return () => window.removeEventListener("trw-super-deals-updated", handler);
  }, []);

  // Filtered by category
  const filtered = useMemo(
    () => (activeCategory === "All" ? deals : deals.filter((d) => d.category === activeCategory)),
    [activeCategory, deals]
  );

  // Visible slice
  const visible = filtered.slice(0, visibleCount);

  // Interleave ads every AD_INTERVAL items
  const grid = useMemo(() => {
    return visible.flatMap((deal, idx) => {
      const items = [{ type: "deal", id: `deal-${deal.id}`, deal }];
      if ((idx + 1) % AD_INTERVAL === 0) {
        items.push({ type: "ad", id: `ad-${idx}` });
      }
      return items;
    });
  }, [visible]);

	  // Category list
	  const categories = useMemo(
	    () => {
	      const preferred = [
	        "Attractions",
	        "Flights",
	        "Hotels",
	        "Indian Escapes Deals",
	        "International Getaway Deals",
	      ];
	      const unique = Array.from(new Set(deals.map((d) => d.category))).filter(Boolean);
	      const sorted = unique.sort((a, b) => {
	        const ia = preferred.indexOf(a);
	        const ib = preferred.indexOf(b);
	        if (ia !== -1 || ib !== -1) {
	          if (ia === -1) return 1;
	          if (ib === -1) return -1;
	          return ia - ib;
	        }
	        return String(a).localeCompare(String(b));
	      });
	      return ["All", ...sorted];
	    },
	    [deals]
	  );

  // Infinite scroll sentinel
  useEffect(() => {
    if (visibleCount >= filtered.length) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisibleCount((c) => Math.min(c + ITEMS_PER_PAGE, filtered.length));
        }
      },
      { root: null, rootMargin: "220px 0px", threshold: 0.01 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [filtered.length, visibleCount]);

	  const handleCategoryChange = (cat) => {
	    setActiveCategory(cat);
	    setVisibleCount(ITEMS_PER_PAGE);
	    const next = new URLSearchParams(location.search);
	    if (!cat || cat === "All") next.delete("cat");
	    else next.set("cat", cat);
	    navigate(
	      { pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : "" },
	      { replace: true }
	    );
	  };

  const openPopup = (deal) => {
    setActiveDeal(deal);
    setShowForm(true);
  };

  const closeAll = () => {
    setShowForm(false);
    setShowCoupon(false);
  };

  const handleFormSuccess = (data) => {
    setCouponData(data);
    setShowForm(false);
    setShowCoupon(true);
  };

  return (
    <div className={`trw-superdeals mx-auto max-w-[1500px] px-4 py-8 ${darkMode ? "bg-black text-white" : "bg-white text-black"}`}>
      {/* Page heading */}

       <h1
            className={`mb-6 text-center text-[32px] leading-[36px] sm:text-[36px] sm:leading-[36px] ${darkMode ? "text-white" : "text-black"}`}
            style={{
              fontFamily: "Poppins, sans-serif",
              fontStyle: "normal",
              fontWeight: 500,
            }}
          >
            150+ Tour Deals
          </h1>

      {/* Filter bar */}
      <FilterBar
        activeCategory={activeCategory}
        setActiveCategory={handleCategoryChange}
        categories={categories}
        darkMode={darkMode}
      />

      {/* Deal grid */}
      <div className="grid grid-cols-1 items-stretch gap-[30px] md:grid-cols-2 lg:grid-cols-3">
        {grid.map((item) =>
          item.type === "deal" ? (
            DealCardComponent ? (
              <DealCardComponent key={item.id} deal={item.deal} darkMode={darkMode} openPopup={openPopup} />
            ) : (
              // Fallback minimal card if no DealCardComponent is provided
              <div
                key={item.id}
                className={`rounded-[18px] p-4 text-sm ${darkMode ? "bg-[#1a1a1a] text-white" : "bg-[#F8F9FA] text-black"}`}
              >
                <p className="font-bold">{item.deal.displayTitle || item.deal.title}</p>
                <p className={darkMode ? "text-[#a0a0a0]" : "text-[#6b7280]"}>{item.deal.category}</p>
              </div>
            )
          ) : (
            <AdComponent key={item.id} shortcode="[trw_ad_slider]" />
          )
        )}
      </div>

      {/* Empty state */}
      {!filtered.length && (
        <div className={`mt-8 rounded-2xl border px-5 py-8 text-center text-sm font-medium ${
          darkMode 
            ? "border-[#333] bg-[#1a1a1a] text-[#b0b0b0]" 
            : "border-[#e5e7eb] bg-[#f8fafc] text-[#4b5563]"
        }`}>
          No deals are available for this category yet.
        </div>
      )}

      {/* Infinite scroll sentinel */}
      {visibleCount < filtered.length && (
        <div ref={sentinelRef} className="h-1 w-full" aria-hidden="true" />
      )}

      {/* Deal enquiry popup */}
      {showForm && (
        <DealPopupForm
          deal={activeDeal}
          close={() => setShowForm(false)}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Coupon success popup */}
      {showCoupon && (
        <CouponPopup
          coupon={couponData.coupon}
          discountPercent={couponData.discountPercent}
          validity={couponData.validity}
          close={closeAll}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// NAMED EXPORTS (for selective use)
// ─────────────────────────────────────────────────────────────

export { FilterBar, DealPopupForm, CouponPopup, DefaultAdComponent };
